import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  AnalyticsTool,
  UserNS,
  extractPrefixedTeamId,
} from "@smart/bridge-types-basic";
import {
  FieldList,
  Modal,
  Select,
  TextFieldInput,
} from "@smart/itops-sb-design-system-dom";
import { buildDisplayName, removeKeys } from "@smart/itops-utils-basic";

import { AnalyticsConfig } from "./analytics-config";
import {
  GenerateForm,
  SubmissionLink,
  Staff,
  ChangeSettingsForm,
  changeSettingsSchema,
} from "./types";

export type ChangeSettingsProps = {
  loading?: boolean;
  editingLink?: SubmissionLink;
  form?: GenerateForm;
  staff: Staff[];
  user?: Staff;
  onSave: (fields: {
    formUri: string;
    slugUri: string;
    notifiedUserUris: string[];
    analyticsTools?: AnalyticsTool[];
  }) => Promise<void>;
  onDone: () => void;
  editModal: [boolean, (v: boolean) => void];
};

export const ChangeSettings = ({
  loading,
  editingLink,
  form,
  user,
  staff,
  onSave,
  onDone,
  editModal,
}: ChangeSettingsProps) => {
  const [analyticsTools, setAnalyticsTools] = useState<
    AnalyticsTool[] | undefined
  >(undefined);
  const isSaving = useRef(false);

  const { control, handleSubmit, reset, setValue } =
    useForm<ChangeSettingsForm>({
      defaultValues: {
        formName: form?.values.title || "",
        staff: [],
      },
      resolver: zodResolver(changeSettingsSchema),
    });

  useEffect(() => {
    // To prevent re-rendering existing data
    if (isSaving.current) return;

    if (editingLink?.notifiedUserUris?.length) {
      const existingNotifiedIds = editingLink.notifiedUserUris.flatMap(
        (userUri) => {
          const { id } = extractPrefixedTeamId(userUri, "sb");
          return id && staff.some((s) => s.userId === id) ? [id] : [];
        },
      );
      setValue("staff", existingNotifiedIds);
    } else {
      setValue("staff", user ? [user.userId] : []);
    }

    if (editingLink?.analyticsTools?.length) {
      setAnalyticsTools(
        editingLink.analyticsTools.map((tool) =>
          removeKeys(tool, ["__typename"]),
        ) as AnalyticsTool[],
      );
    }
  }, [user, editingLink, setValue]);

  if (!editingLink || !form) return null;

  const handleOnDone = () => {
    reset();
    onDone();
  };

  const onSubmit = handleSubmit(async (data) => {
    isSaving.current = true;
    await onSave({
      formUri: editingLink.formUri,
      slugUri: form.slug?.uri || "",
      notifiedUserUris: (data.staff || []).map((id) =>
        UserNS.generateUri("sb", id),
      ),
      analyticsTools,
    });
    isSaving.current = false;
    onDone();
  });

  return (
    <Modal
      header={{
        text: "Change Settings",
        icon: "solidGear",
        iconBgColor: "bg-cyan-140",
      }}
      loading={loading || isSaving.current}
      open={editModal[0]}
      onClose={handleOnDone}
      footer={{
        buttons: [
          {
            text: "Cancel",
            variant: "secondarySubtle",
            onClick: handleOnDone,
          },
          {
            text: "Update",
            onClick: onSubmit,
            disabled: loading || isSaving.current,
          },
        ],
      }}
    >
      <FieldList>
        <Controller
          control={control}
          name="formName"
          render={({ field, fieldState }) => (
            <TextFieldInput
              id={field.name}
              label="Form"
              message={fieldState.error?.message}
              error={!!fieldState.error?.message}
              value={form?.values.title}
              disabled
              readOnly
            />
          )}
        />
        <Controller
          control={control}
          name="staff"
          render={({ field, fieldState }) => (
            <Select
              id={field.name}
              label="Staff"
              options={staff.map((s) => ({
                label: buildDisplayName(s, s.userId),
                value: s.userId,
              }))}
              message={
                fieldState.error
                  ? fieldState.error.message
                  : "Selected staff will be notified when this form is completed"
              }
              error={!!fieldState.error?.message}
              value={field.value}
              onChange={(vals) => {
                let newValues: string[] = [];
                if (Array.isArray(vals)) newValues = vals;
                else if (vals) newValues = [vals];
                field.onChange(newValues);
              }}
              empty="No Staff Available"
              disabled={loading}
              dataTestId="tags-staff"
              maxHeight="medium"
              isMultiple
            />
          )}
        />
        <AnalyticsConfig
          config={analyticsTools}
          setConfig={setAnalyticsTools}
        />
      </FieldList>
    </Modal>
  );
};
